import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  agreeAndConfirmCollaborationRequest,
  getOnboardingCompanyDataRequest,
  getOnboardingProgressRequest,
  postOnboardingAccountStepRequest,
  postOnboardingAddressStepRequest,
  postOnboardingCompanyStepRequest,
  postOnboardingContactStepRequest,
} from 'api/requests/company-onboarding-requests';
import axios from 'axios';
import { OnboardingCompanyProgress } from 'constants/OnboardingCompanyProgress';
import { OnboardingCompanyStatus } from 'constants/OnboardingEnums';
import {
  OnboardingFlowAccountStepFields,
  OnboardingFlowAddressStepFields,
  OnboardingFlowCompanyStepFields,
  OnboardingFlowContactStepFields,
} from 'modules/onboarding/types';
import {
  ONBOARDING_COMPANY__ACCOUNT_STEP,
  ONBOARDING_COMPANY__ADDRESS_STEP,
  ONBOARDING_COMPANY__AGREE_AND_CONFIRM_COLLABORATION,
  ONBOARDING_COMPANY__COMPANY_STEP,
  ONBOARDING_COMPANY__CONTACT_STEP,
  ONBOARDING_COMPANY__GENERAL_LOADING,
  ONBOARDING_COMPANY__GET_BY_ID,
  ONBOARDING_COMPANY__GET_ONBOARDING_PROGRESS,
  ONBOARDING_COMPANY__SET_ONBOARDING_PROGRESS,
} from 'store/constants';

export const setOnboardingCompanyRequestsLoading = createAction<boolean>(ONBOARDING_COMPANY__GENERAL_LOADING);

export const getOnboardingCompanyData = createAsyncThunk(ONBOARDING_COMPANY__GET_BY_ID, async (companyId: number) => {
  const response = await getOnboardingCompanyDataRequest(companyId);
  return response;
});

export const sendOnboardingCompanyStepAction = createAsyncThunk(
  ONBOARDING_COMPANY__COMPANY_STEP,
  async (props: { data: OnboardingFlowCompanyStepFields; companyIdNumber: number }) => {
    const { data, companyIdNumber } = props;
    try {
      await postOnboardingCompanyStepRequest(data, companyIdNumber ?? 0);
      return OnboardingCompanyStatus.CAN_START_ONBOARDING;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 409) {
          return error.response.data;
        }
      }
      console.error('error', error);
    }

    return null;
  },
);

export const sendOnboardingAddressStepAction = createAsyncThunk(
  ONBOARDING_COMPANY__ADDRESS_STEP,
  async (props: { data: OnboardingFlowAddressStepFields; companyIdNumber: number }) => {
    const { data, companyIdNumber } = props;
    const response = await postOnboardingAddressStepRequest(data, companyIdNumber);
    return response;
  },
);

export const sendOnboardingContactStepAction = createAsyncThunk(
  ONBOARDING_COMPANY__CONTACT_STEP,
  async ({ data, companyIdNumber }: { data: OnboardingFlowContactStepFields; companyIdNumber: number }, thunkApi) => {
    try {
      const response = await postOnboardingContactStepRequest(data, companyIdNumber);
      return response;
    } catch (error: any) {
      console.error('Error in sendOnboardingContactStepAction', error);
      return thunkApi.rejectWithValue(error?.response.data);
    }
  },
);

export const sendOnboardingAccountStepAction = createAsyncThunk(
  ONBOARDING_COMPANY__ACCOUNT_STEP,
  async (
    { data, companyIdNumber }: { data: OnboardingFlowAccountStepFields; companyIdNumber: number },
    { rejectWithValue },
  ) => {
    try {
      const userId = await postOnboardingAccountStepRequest(data, companyIdNumber);
      return userId;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const { status, data: responseData } = error.response;

        if (status === 400 && responseData) {
          return rejectWithValue(responseData);
        }

        if (status === 409) {
          return rejectWithValue(responseData);
        }

        return rejectWithValue('An unexpected error occurred');
      }
      console.error('Unexpected error in sendOnboardingAccountStepAction:', error);
      return rejectWithValue('An unexpected error occurred');
    }
  },
);

export const agreeAndConfirmCollaborationAction = createAsyncThunk(
  ONBOARDING_COMPANY__AGREE_AND_CONFIRM_COLLABORATION,
  async (props: { companyIdNumber: number }, thunkApi) => {
    const { companyIdNumber } = props;
    thunkApi.dispatch(setOnboardingCompanyRequestsLoading(true));
    try {
      const companyId = await agreeAndConfirmCollaborationRequest(companyIdNumber);
      thunkApi.dispatch(setOnboardingCompanyRequestsLoading(false));
      return companyId;
    } catch (error) {
      console.error('error', error);
    }
    thunkApi.dispatch(setOnboardingCompanyRequestsLoading(false));
    return null;
  },
);

export const setCompanyOnboardingProgressAction = createAction<OnboardingCompanyProgress>(
  ONBOARDING_COMPANY__SET_ONBOARDING_PROGRESS,
);

export const getCompanyOnboardingProgressAction = createAsyncThunk(
  ONBOARDING_COMPANY__GET_ONBOARDING_PROGRESS,
  async (props: { companyId: number }, thunkApi) => {
    const { companyId } = props;
    thunkApi.dispatch(setOnboardingCompanyRequestsLoading(true));
    try {
      const response = await getOnboardingProgressRequest(companyId);
      thunkApi.dispatch(setCompanyOnboardingProgressAction(response));
    } catch (error) {
      console.error('error', error);
    }
    thunkApi.dispatch(setOnboardingCompanyRequestsLoading(false));
  },
);
